/** Tab 7: Bank */
import { useState } from 'react'
import {
  Button, Card, Form, Input, message, Space, Select, Switch, Row, Col,
} from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { joiningApi, type JoiningDetail } from '@/features/hr/joiningApi'
import { INDIAN_BANKS } from '@/shared/types/hr'

interface Props { detail: JoiningDetail }

const BANK_OPTIONS = INDIAN_BANKS.map((b) => ({ label: b, value: b }))

export default function BankTab({ detail }: Props) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [dirty, setDirty] = useState(false)

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => joiningApi.update(detail.id, values),
    onSuccess: () => {
      message.success('Saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const initial: Record<string, unknown> = { ...detail.employee }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initial}
      onValuesChange={() => setDirty(true)}
      onFinish={(values) => {
        const out: Record<string, unknown> = {}
        Object.keys(values).forEach((k) => {
          const v = values[k]
          out[k] = dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v
        })
        save.mutate(out)
      }}
    >
      <Card size="small" title="Bank Account" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="bank_name"
              label="Bank Name"
              tooltip="Pick from common Indian banks. Type to search — small co-operative banks not listed will still match as free text."
            >
              <Select
                showSearch
                allowClear
                placeholder="Pick or search bank"
                options={BANK_OPTIONS}
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="bank_branch" label="Branch">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="bank_account" label="Account No">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="ifsc" label="IFSC">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="bank_account_type" label="Account Type">
              <Select
                options={[
                  { value: 'SAVINGS', label: 'Savings' },
                  { value: 'CURRENT', label: 'Current' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="account_holder_name" label="Account Holder Name">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="account_verified" label="Account Verified" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="bank_proof_uploaded" label="Bank Proof Uploaded" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Space>
        <Button type="primary" htmlType="submit" disabled={!dirty} loading={save.isPending}>
          Save
        </Button>
        {dirty && <span style={{ color: '#faad14' }}>Unsaved changes</span>}
      </Space>
    </Form>
  )
}
